// Iter61m — MobileRoadmapPage — refonte iPhone : liste clients + steps de livraison interactifs.
// Uses /api/projects + /api/client-delivery (same as desktop), NOT /api/users?role=client.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatDay } from '../lib/dates';
import { Search, X, Check, ArrowLeft, ChevronRight, ProjectIcon, Pencil, Clock } from '../components/icons';


export default function MobileRoadmapPage() {
  const [projects, setProjects] = useState([]);
  const [deliveryMap, setDeliveryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');

  const reload = async () => {
    try {
      const [rp, rd] = await Promise.all([
        api.get('/projects'),
        api.get('/client-delivery').catch(() => ({ data: [] })),
      ]);
      setProjects(rp.data || []);
      const map = {};
      for (const d of (rd.data || [])) map[d.client_key] = d;
      setDeliveryMap(map);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const clients = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const c = (p.client || '—').trim();
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(p);
    }
    // Include standalone roadmaps (delivery docs without matching project)
    for (const [key, doc] of Object.entries(deliveryMap)) {
      const displayName = doc.client_name || key;
      if (!map.has(displayName) && !Array.from(map.keys()).some((k) => k.toLowerCase() === key)) {
        map.set(displayName, []);
      }
    }
    const arr = Array.from(map.entries()).map(([client, ps]) => {
      const active = ps.filter((p) => (p.progress?.ratio || 0) < 1);
      const done = ps.filter((p) => (p.progress?.ratio || 0) >= 1);
      const nextDeadline = ps.map((p) => p.end_date).filter(Boolean).sort()[0];
      const avgRatio = ps.length ? (ps.reduce((a, p) => a + (p.progress?.ratio || 0), 0) / ps.length) : 0;
      const dkey = client.toLowerCase();
      const doc = deliveryMap[dkey] || {};
      const display = doc.display_name || client;
      const steps = doc.steps || [];
      const stepsDone = steps.filter((s) => s.checked).length;
      const stepsTotal = steps.length || 10;
      return { client, display, projects: ps, active, done, nextDeadline, avgRatio, steps, stepsDone, stepsTotal };
    });
    arr.sort((a, b) => {
      if (a.active.length !== b.active.length) return b.active.length - a.active.length;
      return (a.nextDeadline || '9999').localeCompare(b.nextDeadline || '9999');
    });
    return arr;
  }, [projects, deliveryMap]);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return clients;
    return clients.filter((c) => (c.display || c.client || '').toLowerCase().includes(q));
  }, [clients, search]);

  const currentClient = clients.find((c) => c.client === selectedClient);

  // Detail view
  if (currentClient) {
    return (
      <ClientDetailView
        client={currentClient}
        onBack={() => setSelectedClient(null)}
        onStepToggle={async (stepId, checked) => {
          try {
            const r = await api.patch(`/client-delivery/${encodeURIComponent(currentClient.client)}`, { step_id: stepId, checked });
            setDeliveryMap((m) => ({ ...m, [r.data.client_key]: r.data }));
          } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        }}
        onStepNotes={async (stepId, notes) => {
          try {
            const r = await api.patch(`/client-delivery/${encodeURIComponent(currentClient.client)}`, { step_id: stepId, notes });
            setDeliveryMap((m) => ({ ...m, [r.data.client_key]: r.data }));
          } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        }}
      />
    );
  }

  return (
    <div style={{ padding: '16px 14px 90px', minHeight: '100dvh', background: 'var(--bg)' }} data-testid="mobile-roadmap-page">
      <h1 className="asa-display" style={{ fontSize: 26, margin: '2px 0 4px', lineHeight: 1.1 }}>Roadmap</h1>
      <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginBottom: 14 }}>
        {clients.length} client{clients.length > 1 ? 's' : ''} · {projects.length} projet{projects.length > 1 ? 's' : ''}
      </div>

      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', borderRadius: 999,
        background: 'var(--sf2)', border: '1px solid var(--bd)',
        marginBottom: 14,
      }}>
        <Search size={16} color="var(--t3)" />
        <input
          data-testid="mobile-roadmap-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un client"
          style={{ flex: 1, background: 'transparent', border: 'none', fontFamily: 'Lexend', fontSize: 15, color: 'var(--t1)', outline: 'none' }}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ padding: 4, background: 'transparent', border: 'none' }}>
            <X size={14} color="var(--t3)" />
          </button>
        )}
      </div>

      {/* Client cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Chargement…</div>
      ) : filteredClients.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13, border: '1.5px dashed var(--bd)', borderRadius: 12, fontFamily: 'Lexend' }}>
          {search ? 'Aucun client trouvé' : 'Aucun client / projet pour l\'instant.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredClients.map((c) => (
            <ClientCard key={c.client} client={c} onOpen={() => setSelectedClient(c.client)} />
          ))}
        </div>
      )}
    </div>
  );
}


function ClientCard({ client, onOpen }) {
  const pct = Math.round((client.avgRatio || 0) * 100);
  const stepsPct = client.stepsTotal ? Math.round((client.stepsDone / client.stepsTotal) * 100) : 0;
  return (
    <button
      onClick={onOpen}
      data-testid={`mobile-roadmap-client-${client.client}`}
      style={{
        width: '100%', textAlign: 'left',
        padding: '14px 14px', borderRadius: 14,
        background: 'var(--sf)',
        border: '1.5px solid var(--bd)',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'linear-gradient(135deg, #2EA3F2 0%, #5BBEFC 100%)',
          display: 'grid', placeItems: 'center',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: '#fff',
          flexShrink: 0,
        }}>{(client.display || client.client || '?').slice(0, 2).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {client.display}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
            {client.active.length} actif{client.active.length > 1 ? 's' : ''} · {client.done.length} terminé{client.done.length > 1 ? 's' : ''}
            {client.nextDeadline && <> · {formatDay(client.nextDeadline, { day: '2-digit', month: 'short' })}</>}
          </div>
        </div>
        <ChevronRight size={18} color="var(--t3)" />
      </div>

      {/* Progress bars */}
      <div style={{ display: 'flex', gap: 10 }}>
        <ProgressPill label="Projets" pct={pct} color="#5BBEFC" />
        <ProgressPill label="Livraison" pct={stepsPct} color="#34D399" />
      </div>
    </button>
  );
}


function ProgressPill({ label, pct, color }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color }}>{pct}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 999, background: 'var(--sf2)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}


function ClientDetailView({ client, onBack, onStepToggle, onStepNotes }) {
  const [editingStep, setEditingStep] = useState(null);
  const steps = client.steps.length ? client.steps : Array.from({ length: 10 }, (_, i) => ({ id: i + 1, title: `Étape ${i + 1}`, checked: false }));

  return (
    <div style={{ padding: '16px 14px 90px', minHeight: '100dvh', background: 'var(--bg)' }} data-testid="mobile-roadmap-detail">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button
          data-testid="mobile-roadmap-back"
          onClick={onBack}
          style={{ padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#5BBEFC' }}
        >
          <ArrowLeft size={22} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="asa-display" style={{ fontSize: 22, margin: 0, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.display}</h1>
          <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
            {client.projects.length} projet{client.projects.length > 1 ? 's' : ''} · {client.stepsDone}/{client.stepsTotal} étapes
          </div>
        </div>
      </div>

      {/* Progress overview */}
      <div style={{
        padding: 14, borderRadius: 14,
        background: 'var(--sf)', border: '1.5px solid var(--bd)',
        marginBottom: 16,
      }}>
        <ProgressPill label="Progression livraison" pct={client.stepsTotal ? Math.round((client.stepsDone / client.stepsTotal) * 100) : 0} color="#34D399" />
      </div>

      {/* Delivery steps */}
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: 'var(--t2)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Étapes de livraison
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
        {steps.map((s, i) => (
          <StepRow
            key={s.id || i}
            step={s}
            index={i}
            onToggle={() => onStepToggle(s.id, !s.checked)}
            onEditNotes={() => setEditingStep(s)}
          />
        ))}
      </div>

      {/* Projects list */}
      {client.projects.length > 0 && (
        <>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: 'var(--t2)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Projets ({client.projects.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {client.projects.map((p) => (
              <ProjectRow key={p.id} project={p} />
            ))}
          </div>
        </>
      )}

      {/* Notes editor */}
      {editingStep && (
        <NotesEditor
          step={editingStep}
          onClose={() => setEditingStep(null)}
          onSave={async (notes) => {
            await onStepNotes(editingStep.id, notes);
            setEditingStep(null);
          }}
        />
      )}
    </div>
  );
}


function StepRow({ step, index, onToggle, onEditNotes }) {
  const done = !!step.checked;
  const hasNotes = !!(step.notes && step.notes.trim());
  return (
    <div style={{
      display: 'flex', gap: 12,
      padding: '11px 12px', borderRadius: 12,
      background: done ? 'rgba(52,211,153,0.10)' : 'var(--sf)',
      border: `1.5px solid ${done ? 'rgba(52,211,153,0.28)' : 'var(--bd)'}`,
    }}>
      <button
        onClick={onToggle}
        data-testid={`mobile-roadmap-step-toggle-${step.id}`}
        style={{
          width: 26, height: 26, borderRadius: '50%',
          background: done ? '#34D399' : 'transparent',
          border: `2px solid ${done ? '#34D399' : 'var(--t3)'}`,
          display: 'grid', placeItems: 'center',
          flexShrink: 0, cursor: 'pointer',
        }}
      >
        {done && <Check size={14} color="#fff" strokeWidth={3} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)',
          textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.75 : 1,
        }}>
          <span style={{ color: 'var(--t3)', marginRight: 6 }}>{String(index + 1).padStart(2, '0')}</span>
          {step.title}
        </div>
        {step.description && (
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 3, lineHeight: 1.4 }}>
            {step.description}
          </div>
        )}
        {hasNotes && (
          <div style={{
            marginTop: 6, padding: '6px 8px', borderRadius: 8,
            background: 'rgba(46,163,242,0.10)', border: '1px solid rgba(46,163,242,0.20)',
            fontFamily: 'Lexend', fontSize: 11, color: '#5BBEFC', fontStyle: 'italic',
          }}>{step.notes}</div>
        )}
      </div>
      <button
        onClick={onEditNotes}
        data-testid={`mobile-roadmap-step-notes-${step.id}`}
        style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'transparent', border: 'none', display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <Pencil size={13} color="var(--t3)" />
      </button>
    </div>
  );
}


function ProjectRow({ project }) {
  const nav = useNavigate();
  const pct = Math.round((project.progress?.ratio || 0) * 100);
  return (
    <div
      onClick={() => { localStorage.setItem('asa_open_project', project.id); nav('/projects'); }}
      data-testid={`mobile-roadmap-project-${project.id}`}
      style={{
        display: 'flex', gap: 12, alignItems: 'center',
        padding: '11px 12px', borderRadius: 12,
        background: 'var(--sf)', border: '1.5px solid var(--bd)',
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: 'rgba(46,163,242,0.16)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <ProjectIcon size={16} color="#5BBEFC" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {project.name || 'Sans titre'}
        </div>
        <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span>{project.progress?.done || 0}/{project.progress?.total || 0} tâches</span>
          {project.end_date && <>
            <span>·</span>
            <Clock size={9} />
            <span>{formatDay(project.end_date, { day: '2-digit', month: 'short' })}</span>
          </>}
        </div>
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: pct >= 100 ? '#34D399' : '#5BBEFC', flexShrink: 0 }}>{pct}%</div>
    </div>
  );
}


function NotesEditor({ step, onClose, onSave }) {
  const [notes, setNotes] = useState(step.notes || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSave(notes);
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6,10,18,0.65)', backdropFilter: 'blur(6px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--sf)', borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: '18px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)',
          maxHeight: '82vh', overflowY: 'auto',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 4, background: 'var(--t4)', margin: '0 auto 14px' }} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 4 }}>Note pour l&apos;étape</div>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginBottom: 12 }}>{step.title}</div>
        <textarea
          data-testid="mobile-roadmap-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ajoute une note…"
          autoFocus
          rows={5}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 10,
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            fontFamily: 'Lexend', fontSize: 15, color: 'var(--t1)', outline: 'none',
            marginBottom: 14, resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '13px 10px', borderRadius: 12,
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>Annuler</button>
          <button
            data-testid="mobile-roadmap-notes-save"
            onClick={submit}
            disabled={busy}
            style={{
              flex: 1, padding: '13px 10px', borderRadius: 12,
              background: '#2EA3F2', border: 'none', color: '#fff',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 14, cursor: 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}
